<template>
  <div class="loan-req-page">
    <v-container class="loan-req-container">
      <!-- Header -->
      <div class="loan-req-header">
        <div class="loan-req-header-icon">
          <v-icon color="white" size="28">mdi-handshake</v-icon>
        </div>
        <div class="loan-req-header-text">
          <h1 class="loan-req-title">Loan Requirements</h1>
          <p class="loan-req-subtitle">Complete the prerequisites to apply</p>
        </div>
      </div>

      <!-- Checklist Card -->
      <v-card class="loan-req-card" elevation="0">
        <v-card-text class="loan-req-card-body">

          <!-- Progress -->
          <div class="loan-req-progress">
            <div class="loan-req-progress-bar">
              <div
                class="loan-req-progress-fill"
                :style="{ width: progressPercent + '%' }"
              />
            </div>
            <span class="loan-req-progress-text">{{ completedSteps }} of {{ totalSteps }} completed</span>
          </div>

          <!-- Step 1: Identity Verification -->
          <div
            class="loan-req-step"
            :class="{
              'loan-req-step--completed': kycApproved,
              'loan-req-step--pending': !kycApproved
            }"
          >
            <div class="loan-req-step-left">
              <div class="loan-req-step-badge" :class="kycApproved ? 'badge--success' : 'badge--warning'">
                <v-icon v-if="kycApproved" color="white" size="18">mdi-shield-check</v-icon>
                <span v-else class="badge-num">1</span>
              </div>
              <div class="loan-req-step-line" :class="{ 'line--active': kycApproved }" />
            </div>

            <div class="loan-req-step-content">
              <div class="loan-req-step-header">
                <h3 class="loan-req-step-title">Identity Verification</h3>
                <v-chip
                  v-if="kycApproved"
                  x-small
                  color="green"
                  text-color="white"
                  class="loan-req-step-chip"
                >
                  <v-icon x-small left>mdi-check</v-icon>
                  Verified
                </v-chip>
                <v-chip
                  v-else
                  x-small
                  color="orange"
                  text-color="white"
                  class="loan-req-step-chip"
                >
                  <v-icon x-small left>mdi-clock-outline</v-icon>
                  Required
                </v-chip>
              </div>

              <p class="loan-req-step-desc">
                {{ kycApproved
                  ? 'Your identity has been verified.'
                  : 'Identity verification is mandatory before applying for any loan. Complete KYC first.'
                }}
              </p>

              <v-btn
                v-if="!kycApproved"
                color="primary"
                rounded
                depressed
                small
                class="loan-req-step-btn"
                @click="goToIdentity"
              >
                <v-icon left small>mdi-shield-account-outline</v-icon>
                Verify My Identity
              </v-btn>
            </div>
          </div>

          <!-- Step 2: Loan Repayment Terms -->
          <div
            class="loan-req-step"
            :class="{
              'loan-req-step--completed': termsAccepted,
              'loan-req-step--pending': !termsAccepted,
              'loan-req-step--locked': !kycApproved
            }"
          >
            <div class="loan-req-step-left">
              <div
                class="loan-req-step-badge"
                :class="termsAccepted ? 'badge--success' : (!kycApproved ? 'badge--disabled' : 'badge--primary')"
              >
                <v-icon v-if="termsAccepted" color="white" size="18">mdi-check</v-icon>
                <span v-else class="badge-num">2</span>
              </div>
            </div>

            <div class="loan-req-step-content">
              <div class="loan-req-step-header">
                <h3 class="loan-req-step-title">Repayment Commitment</h3>
                <v-chip
                  v-if="termsAccepted"
                  x-small
                  color="green"
                  text-color="white"
                  class="loan-req-step-chip"
                >
                  <v-icon x-small left>mdi-check</v-icon>
                  Accepted
                </v-chip>
              </div>

              <p class="loan-req-step-desc">
                Review and accept the loan repayment obligations. This is a legally binding commitment.
              </p>

              <!-- Terms Box -->
              <div class="loan-req-terms-box">
                <div class="loan-req-terms-header">
                  <v-icon small color="primary">mdi-file-document-outline</v-icon>
                  <span>Legal Obligation</span>
                </div>
                <p class="loan-req-terms-text">{{ legalText }}</p>
              </div>

              <!-- Custom Checkbox -->
              <div
                class="loan-req-checkbox-wrap"
                :class="{ 'loan-req-checkbox-wrap--disabled': !kycApproved }"
                @click="kycApproved && (termsAccepted = !termsAccepted)"
              >
                <div class="loan-req-checkbox" :class="{ 'loan-req-checkbox--checked': termsAccepted }">
                  <v-icon v-if="termsAccepted" color="white" size="16">mdi-check</v-icon>
                </div>
                <span class="loan-req-checkbox-label">
                  <strong>I accept the loan repayment terms</strong>
                </span>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on, attrs }">
                    <v-icon
                      small
                      color="grey lighten-1"
                      class="ml-1"
                      v-bind="attrs"
                      v-on="on"
                    >mdi-information-outline</v-icon>
                  </template>
                  <span>{{ legalText }}</span>
                </v-tooltip>
              </div>
            </div>
          </div>

          <!-- Action Footer -->
          <div class="loan-req-footer">
            <div v-if="!kycApproved" class="loan-req-footer-hint">
              <v-icon x-small color="orange darken-2" class="mr-1">mdi-lock-outline</v-icon>
              Complete identity verification to unlock
            </div>
            <v-btn
              block
              x-large
              rounded
              depressed
              color="primary"
              class="loan-req-confirm-btn"
              :disabled="!canConfirm"
              :loading="confirmLoading"
              @click="confirmTerms"
            >
              <v-icon left>mdi-check-circle-outline</v-icon>
              {{ termsAccepted ? 'Confirm & Apply for Loan' : 'Accept Terms to Continue' }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'LoansChecklist',

  data() {
    return {
      kycApproved: false,
      termsAccepted: false,
      confirmLoading: false,
      legalText: (
        "I understand that by taking a loan, I commit to repaying the full amount " +
        "plus applicable interest within the agreed timeframe. In case of default, " +
        "I accept that legal action may be taken against me and that my personal " +
        "information may be shared with relevant authorities for recovery purposes."
      ),
    }
  },

  computed: {
    totalSteps() {
      return 2
    },
    completedSteps() {
      let count = 0
      if (this.kycApproved) count++
      if (this.termsAccepted) count++
      return count
    },
    progressPercent() {
      return (this.completedSteps / this.totalSteps) * 100
    },
    canConfirm() {
      return this.kycApproved && this.termsAccepted
    },
  },

  mounted() {
    this.checkKYCStatus()
  },

  methods: {
  async checkKYCStatus() {
    try {
      const res = await axios.get('/api/v1/home/kyc/status/')
      this.kycApproved = res.data.kyc_status === 'approved'
      // ✅ Initialiser l'état des termes depuis le backend
      this.termsAccepted = res.data.njangi_terms_accepted === true
    } catch (e) {
      this.kycApproved = false
      this.termsAccepted = false
    }
  },
    goToIdentity() {
      this.$router.push('/user_profile/identity')
    },
    // Loans — même chose
    async confirmTerms() {
      if (!this.canConfirm) return
      this.confirmLoading = true
      try {
        await axios.post('/api/v1/home/loans/accept-terms/', {
          terms_accepted: true,
        })
        this.termsAccepted = true
        this._snack('Your repayment commitment has been recorded.', 'green darken-2')

        // ✅ Redirect to home after 1.5s
        setTimeout(() => this.$router.push('/All'), 1500)

        } catch (e) {
          this._snack('Network error. Please try again.', 'red darken-4')
        } finally {
        this.confirmLoading = false
      }
    },
    _snack(message, color) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', color)
      this.$store.commit('activateSnackBar', true)
    },
  },
}
</script>

<style scoped>
/* ── Base ── */
.loan-req-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f0f4f8 0%, #e8eef5 100%);
  padding: 16px 0 40px;
}

.loan-req-container {
  max-width: 640px;
  margin: 0 auto;
}

/* ── Header ── */
.loan-req-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
  padding: 0 4px;
}

.loan-req-header-icon {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(25, 118, 210, 0.3);
  flex-shrink: 0;
}

.loan-req-header-text { min-width: 0; }

.loan-req-title {
  font-size: 1.4rem;
  font-weight: 800;
  color: #0d2b5e;
  margin: 0;
  letter-spacing: -0.3px;
}

.loan-req-subtitle {
  font-size: 0.88rem;
  color: #78909c;
  margin: 4px 0 0;
}

/* ── Card ── */
.loan-req-card {
  border-radius: 24px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06) !important;
  overflow: hidden;
}

.loan-req-card-body {
  padding: 28px !important;
}

/* ── Progress ── */
.loan-req-progress {
  margin-bottom: 28px;
}

.loan-req-progress-bar {
  width: 100%;
  height: 6px;
  background: #e3eaf3;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
}

.loan-req-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #1976d2, #42a5f5);
  border-radius: 3px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.loan-req-progress-text {
  font-size: 0.78rem;
  font-weight: 600;
  color: #78909c;
}

/* ── Steps ── */
.loan-req-step {
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
  position: relative;
}

.loan-req-step:last-of-type {
  margin-bottom: 0;
}

.loan-req-step--locked {
  opacity: 0.5;
  pointer-events: none;
}

.loan-req-step-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.loan-req-step-badge {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  transition: all 0.3s ease;
  z-index: 1;
}

.badge--success {
  background: linear-gradient(135deg, #388e3c, #66bb6a);
  box-shadow: 0 2px 8px rgba(56, 142, 60, 0.3);
}

.badge--warning {
  background: linear-gradient(135deg, #f57c00, #ffb74d);
  box-shadow: 0 2px 8px rgba(245, 124, 0, 0.3);
}

.badge--primary {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

.badge--disabled {
  background: #e3eaf3;
  color: #90a4ae;
}

.badge-num {
  color: #fff;
  font-weight: 700;
}

.loan-req-step-line {
  width: 2px;
  flex: 1;
  min-height: 40px;
  background: #e3eaf3;
  margin: 4px 0;
  transition: background 0.3s ease;
}

.line--active {
  background: linear-gradient(180deg, #388e3c, #e3eaf3);
}

.loan-req-step-content {
  flex: 1;
  padding-bottom: 24px;
  min-width: 0;
}

.loan-req-step-header {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.loan-req-step-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a237e;
  margin: 0;
}

.loan-req-step-chip {
  font-weight: 700 !important;
  letter-spacing: 0.3px !important;
}

.loan-req-step-desc {
  font-size: 0.85rem;
  color: #78909c;
  line-height: 1.6;
  margin: 0 0 12px;
}

.loan-req-step-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
}

/* ── Terms Box ── */
.loan-req-terms-box {
  background: linear-gradient(135deg, #f7faff 0%, #fff 100%);
  border: 1px solid rgba(25, 118, 210, 0.1);
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 16px;
}

.loan-req-terms-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1976d2;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.loan-req-terms-text {
  font-size: 0.8rem;
  color: #546e7a;
  line-height: 1.6;
  margin: 0;
}

/* ── Custom Checkbox ── */
.loan-req-checkbox-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1.5px solid #e3eaf3;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.loan-req-checkbox-wrap:hover {
  border-color: rgba(25, 118, 210, 0.3);
  background: rgba(25, 118, 210, 0.02);
}

.loan-req-checkbox-wrap--disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.loan-req-checkbox {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid #b0bec5;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.loan-req-checkbox--checked {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  border-color: #1976d2;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

.loan-req-checkbox-label {
  font-size: 0.9rem;
  color: #37474f;
}

/* ── Footer ── */
.loan-req-footer {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.loan-req-footer-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #f57c00;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: rgba(245, 124, 0, 0.06);
  border-radius: 10px;
}

.loan-req-confirm-btn {
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px !important;
  font-size: 1rem !important;
  height: 52px !important;
  transition: all 0.2s ease;
}

.loan-req-confirm-btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(25, 118, 210, 0.25);
}

/* ── Responsive ── */
@media (max-width: 600px) {
  .loan-req-page {
    padding: 12px 0 30px;
  }

  .loan-req-container {
    padding: 0 12px;
  }

  .loan-req-header {
    margin-bottom: 16px;
  }

  .loan-req-header-icon {
    width: 44px;
    height: 44px;
    border-radius: 14px;
  }

  .loan-req-title {
    font-size: 1.2rem;
  }

  .loan-req-subtitle {
    font-size: 0.82rem;
  }

  .loan-req-card-body {
    padding: 20px 16px !important;
  }

  .loan-req-step {
    gap: 12px;
  }

  .loan-req-step-badge {
    width: 32px;
    height: 32px;
  }

  .loan-req-step-title {
    font-size: 0.95rem;
  }

  .loan-req-step-desc {
    font-size: 0.82rem;
  }

  .loan-req-terms-box {
    padding: 12px;
  }

  .loan-req-terms-text {
    font-size: 0.78rem;
  }

  .loan-req-confirm-btn {
    font-size: 0.95rem !important;
    height: 48px !important;
  }
}
</style>